import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react'
import {
    WalletTransactionsResponse,
    RawWalletTransactionsResponse,
    AllWalletTabType,
    FetchControl
} from '@/src/types/wallet'
import setCommonHeaders from '@/src/utils/rtkQuery/setCommonHeaders'
import { getWalletChannel } from '@/src/utils/walletChannelUtils'
import { isCleartrip } from '@/src/utils/generalUtils/isCleartrip'
import {
    getWalletRequestOffset,
    shouldSendWalletTypeParam,
    transformCleartripWalletTransactions,
    transformFlyinWalletTransactions
} from '@/src/utils/wallet/transactionsApiUtils'
import { isWrappedWalletTransactionsResponse, mergeWalletTransactions } from '@/src/utils/wallet/multiBrandWalletUtils'
import { getWalletTransactionsPath, isWalletBffPath } from '@/src/utils/wallet/walletApiPaths'

const baseQuery = fetchBaseQuery({
    baseUrl: '',
    credentials: 'include',
    prepareHeaders: headers => {
        const defaultHeaders = setCommonHeaders(headers)
        return defaultHeaders
    }
})

const baseQueryWithRetry = retry(baseQuery, { maxRetries: 0 })

export const walletApi = createApi({
    reducerPath: 'walletApi',
    baseQuery: baseQueryWithRetry,
    tagTypes: ['WalletTransactions'],
    endpoints: builder => ({
        /**
         * Fetch wallet transactions with pagination.
         * Handles both Flyin (type-based tabs) and Cleartrip (ALL tab, offset-based pagination).
         */
        getWalletTransactions: builder.query<
            WalletTransactionsResponse,
            {
                userId: string | number
                currency: string
                type: AllWalletTabType
                offset: number
                size?: number
                fetchControl?: FetchControl
            }
        >({
            query: ({ userId, currency, type, offset, size = 10, fetchControl = 'ALL' }) => {
                const isCleartripBrand = isCleartrip()
                const walletChannel = getWalletChannel()

                const actualOffset = getWalletRequestOffset({
                    isCleartripBrand,
                    offset,
                    size
                })

                const params: Record<string, any> = {
                    offset: actualOffset,
                    size,
                    currency,
                    ...(walletChannel ? { chnl: walletChannel } : {})
                }

                // Cleartrip "ALL" tab: no type filter. Flyin: pass tab type.
                if (
                    shouldSendWalletTypeParam({
                        isCleartripBrand,
                        type
                    })
                ) {
                    params.type = type
                }

                const transactionsPath = getWalletTransactionsPath(userId)
                const useBffApi = isWalletBffPath(transactionsPath)

                return {
                    url: transactionsPath,
                    params,
                    method: 'GET',
                    headers: useBffApi
                        ? {
                              'link-switch': 'ON',
                              'fetch-control': fetchControl
                          }
                        : {}
                }
            },
            transformResponse: (
                response: RawWalletTransactionsResponse | any,
                meta: any,
                arg: { offset: number; size?: number; fetchControl?: FetchControl }
            ): WalletTransactionsResponse => {
                const isCleartripBrand = isCleartrip()

                if (isWrappedWalletTransactionsResponse(response)) {
                    const remaining = Number(meta?.response?.headers?.get('remainingtransactioncount')) || 0
                    return mergeWalletTransactions(response.MAIN, response.LINKED, remaining)
                }

                if (isCleartripBrand) {
                    const remainingCount = Number(meta?.response?.headers?.get('remainingtransactioncount')) || 0

                    return transformCleartripWalletTransactions({
                        rawTransactions: response,
                        offset: arg.offset,
                        size: arg.size || 10,
                        remainingCount
                    })
                }

                return transformFlyinWalletTransactions(response)
            },
            transformErrorResponse: (response: any): WalletTransactionsResponse => {
                return {
                    status: 'FAILURE',
                    transactions: [],
                    totalRecords: 0,
                    message: response?.data?.message || 'Failed to fetch transactions'
                }
            },
            providesTags: (result, error, { type, fetchControl }) => [
                { type: 'WalletTransactions', id: `${type}-${fetchControl || 'ALL'}` }
            ]
        })
    })
})

export const { useGetWalletTransactionsQuery } = walletApi

export default walletApi
